import { orderBy } from 'lodash'
import { BreadcrumbObjectType } from '~/common/generated/apollo-operations'
import { FormV2FieldValues } from '~/components/form-v2/form-filler/types'
import { MatchedSearchResult, SearchHistoryResult } from '~/components/navigation/SearchInputModal'
import { SearchResult } from '~/components/navigation/types'

// Names of keys in local browser storage
const storageKeys = {
  COLUMN_WIDTHS: 'columnWidths',
  RECENT_SEARCHES: 'recentSearches',
  LAST_VIEWED_TAB: 'lastViewedTab', // Stores the last viewed tab for each table, if exists
  LAST_VIEWED_PIVOT_TABLE: 'lastViewedPivotTable', // Stores the last viewed pivot table for each table, if exists
  HAS_ENTERED_DOSSBOT_CODE: 'hasEnteredDossbotCode', // Stores whether the user has entered the dossbot code
  FLOATING_WIDGET_CORNER: 'floatingWidgetCorner', // Stores the corner position of the floating chat widget
  TABLE_BUTTON_NODE_POSITIONS: 'tableButtonNodePositions', // Stores the positions of button nodes in the table settings actions diagram
  RECENT_PAGES: 'recentPages', // Stores recently visited pages for the home page
  FORM_DRAFT: 'formDraft', // Stores in-progress (unsubmitted) FormV2 answers, per form and filler
  TABLE_SEARCH: 'tableSearch', // Stores the current search text for each table, if exists
  CONFIGBASE_NAV_OVERRIDE: 'configbaseNavOverride', // Stores an internal user's local override of the configbaseNav org flag
  SKIP_DELETE_ROW_CONFIRMATION: 'skipDeleteRowConfirmation', // Stores the form table blocks where a filler opted out of confirming row deletes (per tab for guests)
}

// The maximum number of recent searches to store in local storage
const SEARCH_HISTORY_LIMIT = 8

// The maximum number of recent pages to store in local storage
const RECENT_PAGES_LIMIT = 50

/**
 * Constructs the storage key for column widths based on the parent id
 */
function getColumnWidthStorageKey(id: string) {
  return `${storageKeys.COLUMN_WIDTHS}_${id}`
}

/**
 * Constructs the storage key for recent searches based on the user ID
 */
function getRecentSearchesStorageKey(userId: string) {
  return `${storageKeys.RECENT_SEARCHES}_${userId}`
}

/**
 * Constructs the storage key for last viewed tabs based on the user ID
 */
function getLastViewedTabStorageKey(userId: string) {
  return `${storageKeys.LAST_VIEWED_TAB}_${userId}`
}

/**
 * Constructs the storage key for last viewed pivot tables based on the user ID
 */
function getLastViewedPivotTableStorageKey(userId: string) {
  return `${storageKeys.LAST_VIEWED_PIVOT_TABLE}_${userId}`
}

/**
 * Construct the storage key for whether the user has entered the dossbot code
 */
function getHasEnteredDossbotCodeStorageKey(userId: string) {
  return `${storageKeys.HAS_ENTERED_DOSSBOT_CODE}_${userId}`
}

/**
 * Constructs the storage key for floating widget corner position based on the user ID
 */
function getFloatingWidgetCornerStorageKey(userId: string) {
  return `${storageKeys.FLOATING_WIDGET_CORNER}_${userId}`
}

export function storeHasEnteredDossbotCode(userId: string, hasEntered: boolean): void {
  localStorage.setItem(getHasEnteredDossbotCodeStorageKey(userId), hasEntered.toString())
}

export function retrieveHasEnteredDossbotCode(userId: string): boolean {
  return localStorage.getItem(getHasEnteredDossbotCodeStorageKey(userId)) === 'true'
}

/**
 * Given a table column ID and a column width, stores in local storage
 */
export function storeTableColumnWidth(
  tableId: string,
  columnId: string,
  columnWidth: number,
): Record<string, number> {
  const storageKey = getColumnWidthStorageKey(tableId)
  const storedWidthsString = localStorage.getItem(storageKey)
  const storedWidths = storedWidthsString ? JSON.parse(storedWidthsString) : {}

  storedWidths[columnId] = Math.round(columnWidth)
  localStorage.setItem(storageKey, JSON.stringify(storedWidths))

  return storedWidths
}

/**
 * Retrieves the column widths for the provided table ID from local storage
 */
export function retrieveTableColumnWidths(tableId: string): Record<string, number> {
  const storageKey = getColumnWidthStorageKey(tableId)
  const storedWidthsString = localStorage.getItem(storageKey)
  const storedWidths = storedWidthsString ? JSON.parse(storedWidthsString) : {}
  return storedWidths
}

/**
 * Given a form block or category ID and a form field width, stores in local storage
 */
export function storeFormTableColumnWidth(
  formTableId: string,
  formFieldId: string,
  columnWidth: number,
): Record<string, number> {
  const storageKey = getColumnWidthStorageKey(formTableId)
  const storedWidthsString = localStorage.getItem(storageKey)
  const storedWidths = storedWidthsString ? JSON.parse(storedWidthsString) : {}

  storedWidths[formFieldId] = Math.round(columnWidth)
  localStorage.setItem(storageKey, JSON.stringify(storedWidths))

  return storedWidths
}

/**
 * Retrieves the form table column widths for the provided form block or category ID from local storage
 */
export function retrieveFormTableColumnWidths(formTableId: string): Record<string, number> {
  const storageKey = getColumnWidthStorageKey(formTableId)
  const storedWidthsString = localStorage.getItem(storageKey)
  const storedWidths = storedWidthsString ? JSON.parse(storedWidthsString) : {}
  return storedWidths
}

/**
 * Given a pivot table ID, a column key, and a column width, stores in local storage
 */
export function storePivotTableColumnWidth(
  pivotTableId: string,
  columnKey: string,
  columnWidth: number,
): Record<string, number> {
  const storageKey = getColumnWidthStorageKey(pivotTableId)
  const storedWidthsString = localStorage.getItem(storageKey)
  const storedWidths = storedWidthsString ? JSON.parse(storedWidthsString) : {}

  storedWidths[columnKey] = columnWidth
  localStorage.setItem(storageKey, JSON.stringify(storedWidths))

  return storedWidths
}

/**
 * Retrieves the pivot table column widths for the provided pivot table ID from local storage
 */
export function retrievePivotTableColumnWidths(pivotTableId: string): Record<string, number> {
  const storageKey = getColumnWidthStorageKey(pivotTableId)
  const storedWidthsString = localStorage.getItem(storageKey)
  const storedWidths = storedWidthsString ? JSON.parse(storedWidthsString) : {}
  return storedWidths
}

/**
 * Adds a search query to the list of recent searches in local storage for the specified user ID
 */
export function storeRecentSearch(userId: string, option: SearchResult) {
  const storageKey = getRecentSearchesStorageKey(userId)
  const recentSearchesString = localStorage.getItem(storageKey)
  const recentSearches: SearchHistoryResult[] = recentSearchesString
    ? JSON.parse(recentSearchesString)
    : []

  // Add a timestamp to the search option
  const searchWithTimestamp = { ...option, timestamp: Date.now() }

  // Remove any older searches for the same object
  const dedupedSeaches = recentSearches.filter((search) => search.id !== searchWithTimestamp.id)

  // Add the new search to the array
  dedupedSeaches.push(searchWithTimestamp)

  // Sort the searches by timestamp in descending order and limit the size to SEARCH_HISTORY_LIMIT
  const sortedSearches = orderBy(dedupedSeaches, (search) => search.timestamp, ['desc']).slice(
    0,
    SEARCH_HISTORY_LIMIT,
  )

  localStorage.setItem(storageKey, JSON.stringify(sortedSearches))
}

/**
 * Retrieves the list of recent searches for the specified user ID from local storage
 */
export function retrieveRecentSearches(userId: string): MatchedSearchResult[] {
  const storageKey = getRecentSearchesStorageKey(userId)
  const recentSearchesString = localStorage.getItem(storageKey)
  return recentSearchesString ? JSON.parse(recentSearchesString) : []
}

/**
 * Stores the last viewed tab for a specific table and user
 */
export function storeLastViewedTab(userId: string, tableId: string, tabId: string): void {
  const storageKey = getLastViewedTabStorageKey(userId)
  const lastViewedTabsString = localStorage.getItem(storageKey)
  const lastViewedTabs = lastViewedTabsString ? JSON.parse(lastViewedTabsString) : {}

  lastViewedTabs[tableId] = tabId
  localStorage.setItem(storageKey, JSON.stringify(lastViewedTabs))
}

/**
 * Retrieves the last viewed tab for a specific table and user
 */
export function retrieveLastViewedTab(userId: string, tableId: string): string | null {
  const storageKey = getLastViewedTabStorageKey(userId)
  const lastViewedTabsString = localStorage.getItem(storageKey)
  const lastViewedTabs = lastViewedTabsString ? JSON.parse(lastViewedTabsString) : {}

  return lastViewedTabs[tableId] || null
}

/**
 * Stores the last viewed pivot table for a specific table and user
 */
export function storeLastViewedPivotTable(
  userId: string,
  tableId: string,
  pivotTableId: string,
): void {
  const storageKey = getLastViewedPivotTableStorageKey(userId)
  const lastViewedPivotTablesString = localStorage.getItem(storageKey)
  const lastViewedPivotTables = lastViewedPivotTablesString
    ? JSON.parse(lastViewedPivotTablesString)
    : {}

  lastViewedPivotTables[tableId] = pivotTableId
  localStorage.setItem(storageKey, JSON.stringify(lastViewedPivotTables))
}

/**
 * Retrieves the last viewed pivot table for a specific table and user
 */
export function retrieveLastViewedPivotTable(userId: string, tableId: string): string | null {
  const storageKey = getLastViewedPivotTableStorageKey(userId)
  const lastViewedPivotTablesString = localStorage.getItem(storageKey)
  const lastViewedPivotTables = lastViewedPivotTablesString
    ? JSON.parse(lastViewedPivotTablesString)
    : {}

  return lastViewedPivotTables[tableId] || null
}

/**
 * Constructs the storage key for table search text based on the user ID
 */
function getTableSearchStorageKey(userId: string) {
  return `${storageKeys.TABLE_SEARCH}_${userId}`
}

/**
 * Stores the current search text for a specific table and user. An empty search string removes
 * the entry so cleared searches are not restored later.
 */
export function storeTableSearchString(
  userId: string,
  tableId: string,
  searchString: string,
): void {
  const storageKey = getTableSearchStorageKey(userId)
  const tableSearchesString = localStorage.getItem(storageKey)
  const tableSearches = tableSearchesString ? JSON.parse(tableSearchesString) : {}

  if (searchString) {
    tableSearches[tableId] = searchString
  } else {
    delete tableSearches[tableId]
  }
  localStorage.setItem(storageKey, JSON.stringify(tableSearches))
}

/**
 * Retrieves the stored search text for a specific table and user
 */
export function retrieveTableSearchString(userId: string, tableId: string): string | null {
  const storageKey = getTableSearchStorageKey(userId)
  const tableSearchesString = localStorage.getItem(storageKey)
  const tableSearches = tableSearchesString ? JSON.parse(tableSearchesString) : {}

  return tableSearches[tableId] || null
}

/**
 * Stores the corner position of the floating widget for a specific user
 */
export function storeFloatingWidgetCorner(userId: string, corner: string): void {
  localStorage.setItem(getFloatingWidgetCornerStorageKey(userId), corner)
}

/**
 * Retrieves the corner position of the floating widget for a specific user
 */
export function retrieveFloatingWidgetCorner(userId: string): string | null {
  return localStorage.getItem(getFloatingWidgetCornerStorageKey(userId))
}

/**
 * Stores whether configbase nav is forced on or off for a user, overriding the org flag
 */
export function storeConfigbaseNavOverride(userId: string, isEnabled: boolean): void {
  try {
    localStorage.setItem(`${storageKeys.CONFIGBASE_NAV_OVERRIDE}_${userId}`, isEnabled.toString())
  } catch {
    // Storage unavailable (private mode, quota): the override just doesn't persist
  }
}

/**
 * Retrieves a user's configbase nav override, or null when they follow the org flag
 */
export function retrieveConfigbaseNavOverride(userId: string): boolean | null {
  try {
    const value = localStorage.getItem(`${storageKeys.CONFIGBASE_NAV_OVERRIDE}_${userId}`)
    return value === null ? null : value === 'true'
  } catch {
    return null
  }
}

/**
 * Clears a user's configbase nav override so they follow the org flag again
 */
export function clearConfigbaseNavOverride(userId: string): void {
  try {
    localStorage.removeItem(`${storageKeys.CONFIGBASE_NAV_OVERRIDE}_${userId}`)
  } catch {
    // Storage unavailable: nothing to clear
  }
}

/**
 * Constructs the storage key for the form table blocks where a user skips the row-delete
 * confirmation. Guests share one bucket under `guest`, like form drafts.
 */
function getSkipDeleteRowConfirmationStorageKey(userId: string | undefined) {
  return `${storageKeys.SKIP_DELETE_ROW_CONFIRMATION}_${userId ?? 'guest'}`
}

/**
 * Remembers that a user no longer wants to confirm before deleting a row in this form table
 * block. Guests only opt out for the current tab (see resolveFormDraftStorage).
 * Never throws — a storage failure just means they are asked again next time.
 */
export function storeSkipDeleteRowConfirmation(
  userId: string | undefined,
  tableBlockId: string,
): void {
  const storage = resolveFormDraftStorage(userId)
  const storageKey = getSkipDeleteRowConfirmationStorageKey(userId)
  try {
    const storedString = storage.getItem(storageKey)
    const skippedBlocks: Record<string, boolean> = storedString ? JSON.parse(storedString) : {}
    skippedBlocks[tableBlockId] = true
    storage.setItem(storageKey, JSON.stringify(skippedBlocks))
  } catch {
    // Quota exceeded, unparseable entry, or storage disabled in private browsing
  }
}

/**
 * Whether a user opted out of confirming row deletes in this form table block
 */
export function retrieveSkipDeleteRowConfirmation(
  userId: string | undefined,
  tableBlockId: string,
): boolean {
  try {
    const storage = resolveFormDraftStorage(userId)
    const storedString = storage.getItem(getSkipDeleteRowConfirmationStorageKey(userId))
    const skippedBlocks: Record<string, boolean> = storedString ? JSON.parse(storedString) : {}
    return skippedBlocks[tableBlockId] === true
  } catch {
    return false
  }
}

/**
 * Constructs the storage key for table button node positions based on the table ID
 */
function getTableButtonNodePositionsStorageKey(tableId: string) {
  return `${storageKeys.TABLE_BUTTON_NODE_POSITIONS}_${tableId}`
}

export type TableButtonNodePosition = { x: number; y: number }

/**
 * Stores the positions of button nodes for a specific table
 */
export function storeTableButtonNodePositions(
  tableId: string,
  positions: Record<string, TableButtonNodePosition>,
): void {
  const storageKey = getTableButtonNodePositionsStorageKey(tableId)
  localStorage.setItem(storageKey, JSON.stringify(positions))
}

/**
 * Retrieves the positions of button nodes for a specific table
 */
export function retrieveTableButtonNodePositions(
  tableId: string,
): Record<string, TableButtonNodePosition> | null {
  const storageKey = getTableButtonNodePositionsStorageKey(tableId)
  const storedPositionsString = localStorage.getItem(storageKey)
  return storedPositionsString ? JSON.parse(storedPositionsString) : null
}

/**
 * Clears the stored button node positions for a specific table
 */
export function clearTableButtonNodePositions(tableId: string): void {
  const storageKey = getTableButtonNodePositionsStorageKey(tableId)
  localStorage.removeItem(storageKey)
}

// --- Recent Pages ---

export interface RecentPage {
  id: string
  objectType: BreadcrumbObjectType
  name: string
  path: string
  timestamp: number
  parentName?: string
}

function getRecentPagesStorageKey(userId: string) {
  return `${storageKeys.RECENT_PAGES}_${userId}`
}

/**
 * Stores a recently visited page for the specified user ID.
 * Deduplicates by id and limits to RECENT_PAGES_LIMIT entries.
 */
export function storeRecentPage(userId: string, page: Omit<RecentPage, 'timestamp'>) {
  const storageKey = getRecentPagesStorageKey(userId)
  const storedString = localStorage.getItem(storageKey)
  const storedPages: RecentPage[] = storedString ? JSON.parse(storedString) : []

  const pageWithTimestamp: RecentPage = { ...page, timestamp: Date.now() }

  const sorted = [pageWithTimestamp, ...storedPages.filter((p) => p.id !== pageWithTimestamp.id)]
    .toSorted((a, b) => b.timestamp - a.timestamp)
    .slice(0, RECENT_PAGES_LIMIT)

  localStorage.setItem(storageKey, JSON.stringify(sorted))
}

/**
 * Retrieves recently visited pages for the specified user ID.
 */
export function retrieveRecentPages(userId: string): RecentPage[] {
  const storageKey = getRecentPagesStorageKey(userId)
  const storedString = localStorage.getItem(storageKey)
  return storedString ? JSON.parse(storedString) : []
}

// --- Form drafts (in-progress form fillout) ---

/** Identifies one filler's draft of one form variant. */
export interface FormDraftIdentity {
  formId: string
  variantId: string | undefined
  userId: string | undefined
}

export interface FormDraft extends FormDraftIdentity {
  /** Fingerprint of the form definition the draft was filled against; see getFormDraftShape. */
  version: string
  savedAt: number
  values: FormV2FieldValues
}

const FORM_DRAFT_MAX_BYTES = 1_000_000

/** The length of time to keep a form draft in local storage */
const FORM_DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

/** Whether a draft's last save is old enough that it counts as abandoned. */
function isFormDraftExpired(savedAt: number, now: number): boolean {
  return !Number.isFinite(savedAt) || now - savedAt > FORM_DRAFT_MAX_AGE_MS
}

/**
 * Builds the storage key for a form draft. Guests share a per-form key under `guest`, and a form's
 * own definition is keyed under `base`.
 */
function getFormDraftStorageKey({ formId, variantId, userId }: FormDraftIdentity) {
  return `${storageKeys.FORM_DRAFT}_${formId}_${variantId ?? 'base'}_${userId ?? 'guest'}`
}

/**
 * Returns the storage a filler's drafts live in: localStorage for signed-in users, so a draft
 * survives closing the tab, and per-tab sessionStorage for guests, so answers are never left
 * waiting for the next person on a shared device.
 */
export function resolveFormDraftStorage(userId: string | undefined): Storage {
  return userId ? localStorage : sessionStorage
}

/**
 * Writes a draft to storage, keyed by its form and filler. Skips drafts over the size cap and
 * never throws — a storage failure loses the draft rather than breaking the form.
 */
export function storeFormDraft(storage: Storage, draft: FormDraft): void {
  const storageKey = getFormDraftStorageKey(draft)
  try {
    const serialized = JSON.stringify(draft)
    if (serialized.length > FORM_DRAFT_MAX_BYTES) {
      return
    }
    storage.setItem(storageKey, serialized)
  } catch {
    // Quota exceeded, or storage disabled in private browsing
  }
}

/**
 * Reads a form's draft for one filler. Returns null and deletes the stored entry when the draft
 * was filled against a different version of the form, written by someone else, has gone
 * unsaved for longer than FORM_DRAFT_MAX_AGE_MS, or cannot be parsed.
 */
export function retrieveFormDraft(
  storage: Storage,
  identity: FormDraftIdentity,
  version: string,
): FormDraft | null {
  const { formId, userId } = identity
  const storageKey = getFormDraftStorageKey(identity)
  try {
    const storedString = storage.getItem(storageKey)
    if (!storedString) {
      return null
    }

    const draft: FormDraft = JSON.parse(storedString)
    // JSON.stringify drops an undefined userId, so coalesce both sides before comparing
    const isDifferentFiller = (draft.userId ?? undefined) !== (userId ?? undefined)
    if (
      draft.formId !== formId ||
      isDifferentFiller ||
      draft.version !== version ||
      isFormDraftExpired(draft.savedAt, Date.now())
    ) {
      storage.removeItem(storageKey)
      return null
    }

    return draft
  } catch {
    // Unparseable entry — drop it so the form starts clean
    storage.removeItem(storageKey)
    return null
  }
}

/**
 * Clears the stored draft for a form, e.g. after a successful submission. Never throws — a
 * storage failure leaves the draft behind rather than failing the submit that cleared it.
 */
export function clearFormDraft(storage: Storage, identity: FormDraftIdentity): void {
  try {
    storage.removeItem(getFormDraftStorageKey(identity))
  } catch {
    // Storage disabled — the draft outlives the submission, and is dropped on the next version bump
  }
}

/**
 * Deletes every expired form draft in localStorage, whoever wrote it. Drafts are only ever read
 * back under their own form and filler, so an entry left behind by a signed-out, migrated or
 * deactivated user is never reclaimed by the per-form read path; without this sweep those entries
 * accumulate until the origin's quota fills and new drafts silently stop saving.
 *
 * Guest drafts live in sessionStorage and are discarded with the tab, so they need no sweeping.
 * Returns the number of entries removed. Never throws.
 */
export function sweepExpiredFormDrafts(): number {
  const prefix = `${storageKeys.FORM_DRAFT}_`
  const now = Date.now()

  try {
    const expiredKeys = Object.keys(localStorage).filter((key) => {
      if (!key.startsWith(prefix)) {
        return false
      }
      try {
        const storedString = localStorage.getItem(key)
        if (!storedString) {
          return false
        }
        const draft: FormDraft = JSON.parse(storedString)
        return isFormDraftExpired(draft.savedAt, now)
      } catch {
        // Unparseable entry — it can never be restored, so sweep it too
        return true
      }
    })

    expiredKeys.forEach((key) => localStorage.removeItem(key))
    return expiredKeys.length
  } catch {
    // Storage disabled in private browsing — nothing accumulates there either
    return 0
  }
}
